import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { GameKeys } from "../../games/Games";
import { useGamesAPI } from "../../games/context/GamesProvider";
import { usePopUpsAPI } from "../../popups/context/PopUpsProvider";
import { useScoreAPI, useScoreData } from "../../score/context/ScoreProvider";
import { UseScormAPI } from "../../scorm/context/ScormAPIProvider";
import { DialogAPI, DialogData, DialogKey } from "../Dialogs";
import { useDialogReducer } from "./DialogReducer";

//  CONTEXTS
const DialogDataContext = createContext<DialogData>({} as DialogData);
const DialogAPIContext = createContext<DialogAPI>({} as DialogAPI);

export const DialogProvider: FC<PropsWithChildren> = ({ children }) => {
  console.info("DialogContext render");

  //  OTHER CONTEXTS
  const { chapters } = useScoreData();
  const { openPopUp } = usePopUpsAPI();
  const { openGame } = useGamesAPI();
  const { completePOI } = useScoreAPI();
  const { completeScormObjective } = UseScormAPI();

  //  DATA REDUCER
  const { data, dispatch } = useDialogReducer();

  //  API
  const api: DialogAPI = useMemo(
    () => ({
      startNewDialogSequence: (id, chapters) => {
        dispatch({ type: "setId", id, chapters });
      },
      nextDialog: () => {
        dispatch({ type: "incrementIndex" });
      },
      closeDialogSequence: () => {
        dispatch({ type: "setId", id: "" });
      },
    }),
    [dispatch]
  );

  //  LOGIC
  const isDialogAllreadyDone = useCallback(
    (id: DialogKey) => {
      //  check in score if already visited
      const chapterKey = id.split(".")[0];
      return chapters
        .find((chapter) => chapter.id === chapterKey)
        ?.poisDone.find((poi) => poi === data.currentId)
        ? true
        : false;
    },
    [chapters, data.currentId]
  );

  //  we can't use method in reducer,
  //  so we listen to change with this useEffect and react always after the data change
  useEffect(() => {
    //  we check the new dialog type
    //  open game or popup if needed
    if (data.currentDialog) {
      switch (data.currentDialog?.type) {
        case "popup":
          openPopUp(data.currentDialog.key);
          break;
        case "game":
          openGame(data.currentDialog.key as GameKeys);
          break;
        default:
          break;
      }
    }

    //  index superior of dialogs chain length,
    //  end of the dialogs chain, set score
    if (
      data.currentDialogsSequence &&
      data.currentIndex >= data.currentDialogsSequence.length
    ) {
      completePOI(data.currentId);
      completeScormObjective(data.currentId);
    }

    //  if alreadydone, go to popup if need, else close dialog
    if (isDialogAllreadyDone(data.currentId)) {
      const popupIndex = data.currentDialogsSequence?.findIndex(
        (dialog) => dialog.type === "popup"
      );

      //  go to popup
      if (data.currentIndex === 0 && popupIndex && popupIndex !== -1) {
        dispatch({ type: "setIndex", index: popupIndex });
      }
      //  close the dialog sequence
      else {
        dispatch({ type: "setId", id: "" });
      }
    }
  }, [
    data.currentIndex,
    data.currentDialog,
    data.currentDialogsSequence,
    data.currentId,
    openGame,
    openPopUp,
    dispatch,
    completePOI,
    completeScormObjective,
    isDialogAllreadyDone,
  ]);

  return (
    <DialogAPIContext.Provider value={api}>
      <DialogDataContext.Provider value={data}>
        {children}
      </DialogDataContext.Provider>
    </DialogAPIContext.Provider>
  );
};

//  CUSTOM HOOKS
export const useDialogData = () => useContext(DialogDataContext);
export const useDialogAPI = () => useContext(DialogAPIContext);
